<template>
  <span>{{ priceDisplay(price, lang, isMultiCurrencySupported) }}</span>
</template>

<script setup lang="ts">
export interface Price {
  symbol: string;
  multiplier: number;
  amount: number;
}

defineProps({
  price: {
    type: Object as PropType<Price>,
    required: true,
  },
  lang: {
    type: String,
  },
  isMultiCurrencySupported: {
    type: Boolean,
    default: true,
  },
});
</script>

<script lang="ts">
import { universalStorage } from "@/utils/universal-storage";
import { logger } from "@/services/logger";
import { PropType } from "vue";

const getDefaultPricing = (multipliedAmount: number, symbol: string, lang?: string) => {
  const displayAmount = multipliedAmount.toLocaleString(lang, {
    maximumFractionDigits: 3,
  });
  return multipliedAmount >= 0 ? symbol + displayAmount : "-" + symbol + displayAmount.slice(1);
};

export const priceDisplay = (price: Price, lang?: string, isMultiCurrencySupported: boolean = true) => {
  const multipliedAmount = price.amount / price.multiplier;
  if (!isMultiCurrencySupported) {
    return getDefaultPricing(multipliedAmount, price.symbol, lang);
  }

  const userCurrencyData = universalStorage.get("user_currency");
  const { currency_code, locale } = userCurrencyData ?? {};

  if (!currency_code || !locale) {
    return getDefaultPricing(multipliedAmount, price.symbol, lang);
  }

  try {
    const formattedNumber = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency_code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 3,
      numberingSystem: "latn",
    }).format(multipliedAmount);
    return formattedNumber;
  } catch (err) {
    logger.warn("price.ts -> priceDisplay -> Failed to format pricing, showing default", err);
    return getDefaultPricing(multipliedAmount, price.symbol, lang);
  }
};

export const priceAsNumber = (price: Price) => {
  return price.amount / price.multiplier;
};

export const getPriceObjFromAmount = (amount: number, symbol: string) => {
  return {
    symbol,
    multiplier: 1,
    amount,
  };
};
</script>
